import { Button } from "@shared/components/ui/Button";
import React, { useEffect, useState } from "react";
import theme from "../../../shared/utils/themeColors";
import OnboardingLayout from "../components/OnboardingLayout";
import { useOnboarding } from "../hooks/useOnboarding";
import { OnboardingStep, OrganizationType } from "../types";

/**
 * First step of onboarding - Organization type selection
 */
const Onboarding01Page: React.FC = () => {
  const {
    organizationType,
    receiveNotifications: storedReceiveNotifications,
    loading,
    error,
    saveOrganizationType,
    setError,
    initializeOnboarding,
    isInvitedUser,
  } = useOnboarding();

  const [selectedType, setSelectedType] = useState<OrganizationType | null>(
    organizationType || null
  );
  const [receiveNotifications, setReceiveNotifications] = useState<boolean>(
    typeof storedReceiveNotifications === "boolean"
      ? storedReceiveNotifications
      : false
  );

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType) {
      setError("Please select an organization type");
      return;
    }

    saveOrganizationType(selectedType, receiveNotifications);
  };

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const progress = await initializeOnboarding();

        if (isMounted && progress) {
          if (progress.organizationType) {
            setSelectedType(progress.organizationType);
          }

          if (typeof progress.receiveNotifications === "boolean") {
            setReceiveNotifications(progress.receiveNotifications);
          }
        }
      } catch (error) {
        console.error("Error initializing onboarding:", error);
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <OnboardingLayout currentStep={OnboardingStep.ORGANIZATION_TYPE}>
      <h1 className={`text-3xl ${theme.text.primary} mb-6`}>
        {isInvitedUser ? "Welcome to GCCA" : "Tell us about your organization"}
      </h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {isInvitedUser ? (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700/60 shadow-sm">
              <div className="flex items-center justify-center mb-4">
                <svg
                  className={`fill-current ${theme.text.primary}`}
                  xmlns="http://www.w3.org/2000/svg"
                  width={48}
                  height={48}
                  viewBox="0 0 24 24"
                >
                  <path d="M13 22V11a3 3 0 0 1 3-3h5a3 3 0 0 1 3 3v13H0V14a3 3 0 0 1 3-3h5a3 3 0 0 1 3 3v8h2Zm6-15h-2V3a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7H5V3a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v4Z" />
                </svg>
              </div>
              <p className={`text-center ${theme.text.primary} text-lg mb-4`}>
                You've been invited to join GCCA as a member of an organization.
              </p>
              <p className={`text-center ${theme.text.secondary} mb-6`}>
                Complete the onboarding process to set up your account and join
                your organization.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="sm:flex space-y-3 sm:space-y-0 sm:space-x-4 mb-8">
              <label className="flex-1 relative block cursor-pointer">
                <input
                  type="radio"
                  name="organization-type"
                  className="peer sr-only"
                  checked={selectedType === OrganizationType.ORGANIZATION}
                  onChange={() =>
                    setSelectedType(OrganizationType.ORGANIZATION)
                  }
                />
                <div className="h-full text-center bg-white dark:bg-gray-800 px-4 py-6 rounded-lg border border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm transition">
                  <svg
                    className={`inline-flex fill-current ${theme.text.primary} mt-2 mb-4`}
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path d="M13 22V11a3 3 0 0 1 3-3h5a3 3 0 0 1 3 3v13H0V14a3 3 0 0 1 3-3h5a3 3 0 0 1 3 3v8h2Zm6-15h-2V3a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7H5V3a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v4ZM9 22v-8a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v8h7Zm13 0V11a1 1 0 0 0-1-1h-5a1 1 0 0 0-1 1v11h7Zm-5-8v-2h3v2h-3Zm0 3v-2h3v2h-3Zm0 3v-2h3v2h-3ZM4 20v-2h3v2H4Zm0-3v-2h3v2H4Z" />
                  </svg>
                  <div className={`font-semibold ${theme.text.primary} mb-1`}>
                    Organization
                  </div>
                  <div className={`text-sm ${theme.text.secondary}`}>
                    Join as an organizational member of GCCA.
                  </div>
                </div>
                <div
                  className={`absolute inset-0 border-2 border-transparent peer-checked:border-blue-500 dark:peer-checked:border-blue-400 rounded-lg pointer-events-none`}
                  aria-hidden="true"
                ></div>
              </label>

              <label className="flex-1 relative block cursor-pointer">
                <input
                  type="radio"
                  name="organization-type"
                  className="peer sr-only"
                  checked={selectedType === OrganizationType.CORPORATE}
                  onChange={() => setSelectedType(OrganizationType.CORPORATE)}
                />
                <div className="h-full text-center bg-white dark:bg-gray-800 px-4 py-6 rounded-lg border border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm transition">
                  <svg
                    className={`inline-flex fill-current ${theme.text.primary} mt-2 mb-4`}
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10a9.955 9.955 0 0 1-2.003 6.005 2 2 0 0 0-1.382-1.115l-3.293-.732-.295-1.178A4.992 4.992 0 0 0 17 11v-1a5 5 0 0 0-10 0v1c0 1.626.776 3.07 1.977 3.983l-.294 1.175-3.293.732a1.999 1.999 0 0 0-1.384 1.119A9.956 9.956 0 0 1 2 12Zm3.61 7.693A9.96 9.96 0 0 0 12 22c2.431 0 4.66-.868 6.393-2.31l-.212-.847-4.5-1-.496-1.984a5.016 5.016 0 0 1-2.365 0l-.496 1.983-4.5 1-.213.85ZM12 7a3 3 0 0 0-3 3v1a3 3 0 1 0 6 0v1a3 3 0 0 0-3-3Z"
                      fillRule="evenodd"
                    />
                  </svg>

                  <div className={`font-semibold ${theme.text.primary} mb-1`}>
                    Corporate Member
                  </div>
                  <div className={`text-sm ${theme.text.secondary}`}>
                    Join as a corporate member of GCCA.
                  </div>
                </div>
                <div
                  className={`absolute inset-0 border-2 border-transparent peer-checked:border-blue-500 dark:peer-checked:border-blue-400 rounded-lg pointer-events-none`}
                  aria-hidden="true"
                ></div>
              </label>
            </div>

            <div className="flex items-center justify-between space-x-6 mb-8">
              <div>
                <div
                  className={`font-medium ${theme.text.primary} text-sm mb-1`}
                >
                  📩 Receive email notifications
                </div>
                <div className={`text-xs ${theme.text.secondary}`}>
                  Stay updated with the latest news, events, and opportunities
                  from GCCA.
                </div>
              </div>
              <div className="flex items-center">
                <div className="form-switch">
                  <input
                    type="checkbox"
                    id="notifications"
                    className="sr-only"
                    checked={receiveNotifications}
                    onChange={() =>
                      setReceiveNotifications(!receiveNotifications)
                    }
                  />
                  <label
                    className="bg-gray-400 dark:bg-gray-700"
                    htmlFor="notifications"
                  >
                    <span
                      className="bg-white shadow-sm"
                      aria-hidden="true"
                    ></span>
                    <span className="sr-only">Notifications toggle</span>
                  </label>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-end">
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? "Saving..." : "Next Step →"}
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  );
};

export default Onboarding01Page;
